import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BrandMark } from '../components/BrandMark';
import { ImageLightbox } from '../components/ImageLightbox';
import { CalendlyInline, CALENDLY_URL } from '../components/CalendlyInline';
import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VidalyticsEmbed = () => {
  const scriptInjected = React.useRef(false);
  
  useEffect(() => {
    // Prevent double injection in React StrictMode
    if (scriptInjected.current) return;
    
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    
    // Note: This is a trusted Vidalytics script, not user-generated content
    // The script URL and embed ID are hardcoded and controlled by us
    script.text = `
      (function (v, i, d, a, l, y, t, c, s) {
        y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
        if (!vsl){vsl=function(u,cb){
          if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
          if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
          i.getElementsByTagName("head")[0].appendChild(s);
        };}
        vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
      })(window, document, 'Vidalytics', 'vidalytics_embed_gLEotFPbLeMpZ9y4', 'https://fast.vidalytics.com/embeds/85Ip_8hv/gLEotFPbLeMpZ9y4/');
    `;
    
    document.body.appendChild(script);
    scriptInjected.current = true;
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []); // Empty deps - only run once
  
  return (
    <div id="vidalytics_embed_gLEotFPbLeMpZ9y4" style={{ width: '100%', position: 'relative', paddingTop: '56.25%' }} data-testid="vsl-video"></div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [zoomed, setZoomed] = useState(null);
  const [stage, setStage] = useState('form'); // form | checking | calendar

  // After submit, ask the backend whether this lead books a call or gets the
  // soft decline. Falls back to the calendar if the webhook is slow.
  const resolveOutcome = async (responseId) => {
    const reveal = () => {
      setStage('calendar');
      setTimeout(() => scrollToForm(), 150);
    };
    if (!responseId) {
      reveal();
      return;
    }
    for (let attempt = 0; attempt < 7; attempt += 1) {
      try {
        const res = await fetch(`${API}/api/application-outcome/${responseId}`);
        const data = await res.json();
        if (data.outcome === 'soft_dq') {
          navigate('/soft-dq');
          return;
        }
        if (data.outcome === 'qualified') {
          reveal();
          return;
        }
      } catch {
        // keep polling — the webhook may still be in flight
      }
      await new Promise((r) => setTimeout(r, 1200));
    }
    reveal();
  };

  // Floating Apply CTA visibility — hidden until scrolled, and hidden while the form is on screen
  useEffect(() => {
    const handleScroll = () => {
      const form = document.getElementById('form');
      const formVisible = form
        ? (() => {
            const r = form.getBoundingClientRect();
            return r.top < window.innerHeight * 0.75 && r.bottom > 0;
          })()
        : false;
      setShowScrollTop(window.scrollY > 500 && !formVisible);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Inject Typeform embed script (once) so the [data-tf-live] widget renders
  useEffect(() => {
    if (document.querySelector('script[data-tf-embed]')) return;
    const s = document.createElement('script');
    s.src = 'https://embed.typeform.com/next/embed.js';
    s.async = true;
    s.setAttribute('data-tf-embed', 'true');
    document.body.appendChild(s);
  }, []);

  // Listen for Typeform submit → fire Pixel 'Lead' and reveal the calendar in place
  useEffect(() => {
    const handleTypeformEvent = (e) => {
      const data = typeof e.data === 'string' ? (() => { try { return JSON.parse(e.data); } catch { return null; } })() : e.data;
      if (data?.type !== 'form-submit') return;
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }
      setStage('checking');
      resolveOutcome(data.responseId || data.response_id || '');
    };
    window.addEventListener('message', handleTypeformEvent);
    return () => window.removeEventListener('message', handleTypeformEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for Calendly booking → Pixel 'Schedule' + confirmation page
  useEffect(() => {
    const handleCalendlyEvent = (e) => {
      if (e.data?.event !== 'calendly.event_scheduled') return;
      if (typeof window.fbq === 'function') {
        const inviteeUuid = (e.data?.payload?.invitee?.uri || '').split('/').pop();
        if (inviteeUuid) {
          window.fbq('track', 'Schedule', {}, { eventID: inviteeUuid });
        } else {
          window.fbq('track', 'Schedule');
        }
      }
      navigate('/confirmation');
    };
    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, [navigate]);

  const scrollToForm = () => {
    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#2C0616]">
      {/* Top Header — Logo + absolutely centered niche tag + Apply CTA */}
      <div className="sticky top-0 z-50 bg-[#2C0616]/95 backdrop-blur-md text-[#F5F0E8] py-3 md:py-5 px-4 md:px-6 border-b-4 border-[#C9A55A]/35">
        <div className="max-w-7xl mx-auto relative flex items-center justify-between gap-2 md:gap-4">
          {/* Logo */}
          <BrandMark testId="header-logo" />

          {/* Niche tag — dead-centered relative to the header itself */}
          <span
            className="prima-glow hidden sm:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-sans text-[13px] md:text-[16px] uppercase tracking-[0.2em] whitespace-nowrap pointer-events-none"
            style={{ fontWeight: 700 }}
            data-testid="niche-tag"
          >
            OnlyFans creators only
          </span>

          {/* Apply Now CTA */}
          <a href="#form" data-testid="nav-apply-now" className="shrink-0">
            <Button
              className="prima-nav-cta uppercase px-4 sm:px-5 lg:px-7 py-4 sm:py-5 lg:py-6 text-xs sm:text-sm lg:text-base"
            >
              Apply Now
            </Button>
          </a>
        </div>

        {/* Mobile fallback: show niche tag on its own thin line */}
        <div className="sm:hidden max-w-7xl mx-auto pt-2 text-center">
          <span
            className="prima-glow font-sans text-[13px] uppercase tracking-[0.18em]"
            style={{ fontWeight: 700 }}
            data-testid="niche-tag-mobile"
          >
            OnlyFans creators only
          </span>
        </div>
      </div>

      {/* VSL Video Section - MAIN HERO */}
      <section className="px-3 py-6 md:px-6 md:py-16 bg-[#2C0616]" id="video" data-animate>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-5 md:mb-12">
            <h1
              className="text-3xl md:text-5xl lg:text-6xl font-sans text-[#F5F0E8] mb-3 md:mb-6"
              style={{ fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.12 }}
              data-testid="hero-headline"
            >
              Income Plateaued or Constant Restrictions?<br />
              <span className="text-[#E0C07A]">Watch How We Scale Creators Without Getting Banned.</span>
            </h1>
            <p
              className="text-base md:text-2xl font-sans text-[#F5F0E8]/70 mb-5 md:mb-12 px-2 italic"
              style={{ fontWeight: 400, letterSpacing: '0.01em' }}
              data-testid="hero-subheadline"
            >
              Do you think you have what it takes to be our next $80K/month creator?
            </p>
          </div>
          
          {/* Vidalytics Embed */}
          <div className="border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden">
            <VidalyticsEmbed />
          </div>
        </div>
      </section>

      {/* Application Form — Typeform embed */}
      <section className="px-4 md:px-6 pt-6 md:pt-8 pb-6 md:pb-8 bg-[#2C0616] scroll-mt-28 md:scroll-mt-32" id="form" data-animate>
        <div className="max-w-3xl mx-auto space-y-8 md:space-y-10">
          <div className="text-center">
            <h2
              className="text-2xl md:text-4xl lg:text-5xl font-sans text-[#F5F0E8]"
              style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
              data-testid="apply-headline"
            >
              {stage === 'calendar' ? 'Book Your Call' : 'Apply Here'}
            </h2>
            <ChevronDown
              className="w-7 h-7 md:w-9 md:h-9 mx-auto mt-3 md:mt-4 text-[#C9A55A] animate-bounce"
              strokeWidth={1.75}
              aria-hidden="true"
              data-testid="apply-arrow"
            />
            {stage === 'calendar' && (
              <p
                className="text-base md:text-lg font-sans text-[#F5F0E8]/70 mt-3 md:mt-4"
                data-testid="booking-subline"
              >
                Your application is in. Pick a time below — slots are first-come, first-served.
              </p>
            )}
          </div>

          <div
            data-tf-live="01K9BQ5W7YZTC9jHX3Yk"
            data-tf-widget="C9jHX3Yk"
            data-tf-inline-on-mobile
            data-tf-medium="snippet"
            className={`border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden ${stage === 'form' ? '' : 'hidden'}`}
            style={{ width: '100%', height: 'clamp(520px, 62vh, 600px)' }}
            data-testid="typeform-embed"
          />

          {stage === 'checking' && (
            <div
              className="border-4 border-[#C9A55A]/35 rounded-[16px] bg-[#220410] flex flex-col items-center justify-center gap-4 text-center px-6"
              style={{ width: '100%', height: 'clamp(520px, 62vh, 600px)' }}
              data-testid="submit-loading"
            >
              <Loader2 className="w-10 h-10 text-[#C9A55A] animate-spin" strokeWidth={2} />
              <p className="font-sans text-[#F5F0E8] text-base md:text-lg" style={{ fontWeight: 600 }}>
                Application received — loading your calendar…
              </p>
            </div>
          )}

          {stage === 'calendar' && (
            <>
              <CalendlyInline
                className="border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden bg-[#2C0616]"
                style={{ width: '100%', minWidth: '320px', height: '760px' }}
              />
              <div className="text-center">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-[#C9A55A] hover:text-[#E0C07A] transition-colors"
                  style={{ fontWeight: 600 }}
                  data-testid="calendly-fallback-link"
                >
                  Or open the calendar in a new tab →
                </a>
              </div>
            </>
          )}
        </div>
      </section>


      {/* Real Results — Actual creator screenshots + photo (simplified) */}
      <section className="px-4 md:px-6 pt-8 md:pt-12 pb-10 md:pb-16 bg-[#2C0616]" id="results" data-animate>
        <div className="max-w-6xl mx-auto">
          {/* Section eyebrow */}
          <div className="flex items-center justify-center gap-4 md:gap-6 mb-8 md:mb-12" data-testid="results-eyebrow">
            <span className="h-px w-6 md:w-6 bg-[#C9A55A]" />
            <span
              className="font-sans text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[#C9A55A] whitespace-nowrap"
              style={{ fontWeight: 500 }}
            >
              Recent Creator Results
            </span>
            <span className="h-px w-6 md:w-6 bg-[#C9A55A]" />
          </div>

          {/* Header */}
          <div className="text-center mb-8 md:mb-12 space-y-4">
            <h2
              className="text-3xl md:text-5xl lg:text-6xl font-sans text-[#F5F0E8] leading-[1.12]"
              style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
              data-testid="results-headline"
            >
              From $1K to $80K/month<br className="hidden md:block" />
              <span className="block md:inline"> in 5 months.</span>
            </h2>

            {/* Small creator avatar + caption row */}
            <div className="flex items-center justify-center gap-3 pt-1" data-testid="creator-avatar-row">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden border-2 border-[#C9A55A]/35 shrink-0 shadow-md">
                <img
                  src="/screenshots/creator-photo.png"
                  alt="Prima Agency creator"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 30%' }}
                  loading="lazy"
                />
              </div>
              <p className="font-sans text-sm md:text-base text-[#F5F0E8]/60 italic">
                Transparent creator transformation
              </p>
            </div>
          </div>

          {/* Two dashboards side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="dashboards">
            {/* BEFORE */}
            <figure
              className="relative bg-white border-2 border-[#C9A55A]/15 rounded-[16px] overflow-hidden shadow-md"
              data-testid="before-shot"
            >
              <div className="flex items-center justify-between px-4 py-3 bg-[#2C0616] border-b border-[#C9A55A]/15">
                <span
                  className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#F5F0E8]/70"
                  style={{ fontWeight: 700 }}
                >
                  Before · Month 1
                </span>
                <span className="font-sans text-sm md:text-base text-[#F5F0E8]/55" style={{ fontWeight: 500 }}>
                  $951
                </span>
              </div>
              <img
                src="/screenshots/gry-before.webp"
                alt="Creator dashboard — Month 1 earnings"
                onClick={() => setZoomed({ src: '/screenshots/gry-before.webp', alt: 'Creator dashboard — Month 1 earnings' })}
                className="w-full h-auto block cursor-zoom-in"
                loading="lazy"
                />
            </figure>

            {/* AFTER */}
            <figure
              className="relative bg-white border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden shadow-2xl"
              data-testid="after-shot"
            >
              <div className="flex items-center justify-between px-4 py-3 bg-[#220410]">
                <span
                  className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#E0C07A]"
                  style={{ fontWeight: 700 }}
                >
                  After · Month 5
                </span>
                <span className="prima-glow font-sans text-base md:text-lg" style={{ fontWeight: 800 }}>
                  $79,446
                </span>
              </div>
              <img
                src="/screenshots/gry-after.webp"
                alt="Creator dashboard — Month 5 earnings"
                onClick={() => setZoomed({ src: '/screenshots/gry-after.webp', alt: 'Creator dashboard — Month 5 earnings' })}
                className="w-full h-auto block cursor-zoom-in"
                loading="lazy"
                />
            </figure>
          </div>
            <p className="md:hidden text-center font-sans text-[11px] uppercase tracking-[0.16em] text-[#E0C07A]/80 mt-3" style={{ fontWeight: 600 }}>Tap a screenshot to zoom</p>

          <div className="mt-12 md:mt-16 mb-12 md:mb-16 h-px bg-gradient-to-r from-transparent via-[#C9A55A]/35 to-transparent" data-testid="case-divider" />
          {/* ——— Case Study 2: $5K → $20K in the first month ——— */}
          <div>
            <div className="text-center mb-8 md:mb-12 space-y-4">
              <h2
                className="text-3xl md:text-5xl lg:text-6xl font-sans text-[#F5F0E8] leading-[1.12]"
                style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
                data-testid="results-headline-2"
              >
                From $5K to $20K/month<br className="hidden md:block" />
                <span className="block md:inline"> in the first month.</span>
              </h2>

              {/* Small creator avatar + caption row */}
              <div className="flex items-center justify-center gap-3 pt-1" data-testid="creator-avatar-row-2">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden border-2 border-[#C9A55A]/35 shrink-0 shadow-md">
                  <img
                    src="/screenshots/creator2-photo.webp"
                    alt="Prima Agency creator"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 25%' }}
                    loading="lazy"
                  />
                </div>
                <p className="font-sans text-sm md:text-base text-[#F5F0E8]/60 italic">
                  Transparent creator transformation
                </p>
              </div>
            </div>

            {/* Two dashboards side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="dashboards-2">
              {/* BEFORE */}
              <figure
                className="relative bg-white border-2 border-[#C9A55A]/15 rounded-[16px] overflow-hidden shadow-md"
                data-testid="before-shot-2"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#2C0616] border-b border-[#C9A55A]/15">
                  <span
                    className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#F5F0E8]/70"
                    style={{ fontWeight: 700 }}
                  >
                    Before Prima
                  </span>
                  <span className="font-sans text-sm md:text-base text-[#F5F0E8]/55" style={{ fontWeight: 500 }}>
                    $5,995
                  </span>
                </div>
                <img
                  src="/screenshots/pernille-before.webp"
                  alt="Creator 2 dashboard — earnings before joining Prima"
                  onClick={() => setZoomed({ src: '/screenshots/pernille-before.webp', alt: 'Creator 2 dashboard — earnings before joining Prima' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>

              {/* AFTER */}
              <figure
                className="relative bg-white border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden shadow-2xl"
                data-testid="after-shot-2"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#220410]">
                  <span
                    className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#E0C07A]"
                    style={{ fontWeight: 700 }}
                  >
                    After · First Month
                  </span>
                  <span className="prima-glow font-sans text-base md:text-lg" style={{ fontWeight: 800 }}>
                    $20,593
                  </span>
                </div>
                <img
                  src="/screenshots/pernille-after.webp"
                  alt="Creator 2 dashboard — first month with Prima"
                  onClick={() => setZoomed({ src: '/screenshots/pernille-after.webp', alt: 'Creator 2 dashboard — first month with Prima' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>
            </div>
            <p className="md:hidden text-center font-sans text-[11px] uppercase tracking-[0.16em] text-[#E0C07A]/80 mt-3" style={{ fontWeight: 600 }}>Tap a screenshot to zoom</p>
          </div>

          <div className="mt-12 md:mt-16 mb-12 md:mb-16 h-px bg-gradient-to-r from-transparent via-[#C9A55A]/35 to-transparent" data-testid="case-divider" />
          {/* ——— Case Study 3: $2K → $21K in the first month ——— */}
          <div>
            <div className="text-center mb-8 md:mb-12 space-y-4">
              <h2
                className="text-3xl md:text-5xl lg:text-6xl font-sans text-[#F5F0E8] leading-[1.12]"
                style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
                data-testid="results-headline-3"
              >
                From $2K to $21K/month<br className="hidden md:block" />
                <span className="block md:inline"> in the first month.</span>
              </h2>

              {/* Small creator avatar + caption row */}
              <div className="flex items-center justify-center gap-3 pt-1" data-testid="creator-avatar-row-3">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden border-2 border-[#C9A55A]/35 shrink-0 shadow-md">
                  <img
                    src="/screenshots/creator3-photo.png"
                    alt="Prima Agency creator"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 25%' }}
                    loading="lazy"
                  />
                </div>
                <p className="font-sans text-sm md:text-base text-[#F5F0E8]/60 italic">
                  Transparent creator transformation
                </p>
              </div>
            </div>

            {/* Two dashboards side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="dashboards-3">
              {/* BEFORE */}
              <figure
                className="relative bg-white border-2 border-[#C9A55A]/15 rounded-[16px] overflow-hidden shadow-md"
                data-testid="before-shot-3"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#2C0616] border-b border-[#C9A55A]/15">
                  <span
                    className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#F5F0E8]/70"
                    style={{ fontWeight: 700 }}
                  >
                    Before Prima
                  </span>
                  <span className="font-sans text-sm md:text-base text-[#F5F0E8]/55" style={{ fontWeight: 500 }}>
                    $2,039
                  </span>
                </div>
                <img
                  src="/screenshots/creator3-before.webp"
                  alt="Creator 3 dashboard — earnings before joining Prima"
                  onClick={() => setZoomed({ src: '/screenshots/creator3-before.webp', alt: 'Creator 3 dashboard — earnings before joining Prima' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>

              {/* AFTER */}
              <figure
                className="relative bg-white border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden shadow-2xl"
                data-testid="after-shot-3"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#220410]">
                  <span
                    className="font-sans text-[11px] md:text-[13px] uppercase tracking-[0.18em] text-[#E0C07A]"
                    style={{ fontWeight: 700 }}
                  >
                    After · First Month
                  </span>
                  <span className="prima-glow font-sans text-base md:text-lg" style={{ fontWeight: 800 }}>
                    $21,145
                  </span>
                </div>
                <img
                  src="/screenshots/creator3-after.webp"
                  alt="Creator 3 dashboard — first month with Prima"
                  onClick={() => setZoomed({ src: '/screenshots/creator3-after.webp', alt: 'Creator 3 dashboard — first month with Prima' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>
            </div>
            <p className="md:hidden text-center font-sans text-[11px] uppercase tracking-[0.16em] text-[#E0C07A]/80 mt-3" style={{ fontWeight: 600 }}>Tap a screenshot to zoom</p>
          </div>
        </div>
      </section>

      {/* Blue Divider */}
      <div className="w-full border-t-4 border-[#C9A55A]/35"></div>

      {/* Footer — Minimal, no navigation or social */}
      <footer className="bg-[#220410] border-t border-[#C9A55A]/15 py-10 md:py-14 px-6">
        <div className="max-w-7xl mx-auto text-center space-y-3">
          <h3 className="text-xl md:text-2xl font-serif uppercase text-[#C9A55A]" style={{ fontWeight: 400, letterSpacing: '0.02em' }}>
            Prima Agency
          </h3>
          <p className="font-sans text-xs md:text-sm text-[#F5F0E8]/38">
            © 2026 Prima Agency. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating Apply Now Button — follows the visitor down the page */}
      {showScrollTop && (
        <button
          onClick={scrollToForm}
          className="fixed bottom-6 right-4 md:bottom-8 md:right-8 bg-[#E0C07A] text-[#1F0410] border border-[#E0C07A] hover:bg-[#C9A55A] hover:border-[#C9A55A] pl-5 pr-4 py-3 md:pl-6 md:pr-5 md:py-4 rounded-full shadow-2xl transition-colors duration-300 z-50 flex items-center gap-2 uppercase font-sans text-xs md:text-sm tracking-[0.12em]"
          style={{ fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          aria-label="Apply now — jump to the application form"
          data-testid="floating-apply-now"
        >
          Apply Now
          <ChevronUp className="w-4 h-4 md:w-5 md:h-5 prima-bounce" strokeWidth={2.5} />
        </button>
      )}

      {zoomed && (
        <ImageLightbox
          src={zoomed.src}
          alt={zoomed.alt}
          label={zoomed.alt}
          onClose={() => setZoomed(null)}
        />
      )}
    </div>
  );
};

export default LandingPage;
